import { Buffer } from 'buffer';
import { AES } from 'crypto-js';
import jwtDecode from 'jwt-decode';
import jwtEncode from 'jwt-encode';

import { useReactQueryClient } from '@spazious/react-query';

import { useBackofficeMutation } from '../context';
import { BackofficeMutationHookOptions } from '../context';

export interface BackofficeSignInMutationData {
  readonly accessToken: string;
  readonly refreshToken: string;
}

export type BackofficeSignInMutationVariables =
  | {
      readonly refreshToken: string;
    }
  | {
      readonly username: string;
      readonly password: string;
      readonly refreshToken?: undefined;
    };

export type BackofficeSignInMutationHookOptions = Omit<
  BackofficeMutationHookOptions<BackofficeSignInMutationData, BackofficeSignInMutationVariables>,
  'mutationKey'
>;

const ES = Uint8Array.from([
  13, 107, 8, 238, 245, 193, 25, 89, 109, 191, 159, 71, 215, 163, 96, 249, 54, 71, 167, 152, 93, 77,
  45, 140, 11, 136, 155, 249, 106, 124, 195, 174, 178, 28, 36, 74, 53, 38, 187, 121, 35, 105, 52,
  209, 221, 10, 132, 28, 89, 137,
]);

const TS = Uint8Array.from([
  28, 104, 71, 192, 119, 24, 187, 27, 121, 203, 7, 83, 172, 63, 204, 238, 246, 143, 62, 78, 173, 39,
  65, 33, 16, 109, 201, 125, 195, 213, 247, 227, 221, 55, 234, 176, 193, 15, 62, 30, 19, 138, 165,
  187, 155, 167, 180, 81, 217, 161,
]);

/**
 * Hook for deleting an account,
 */
export const useSignIn = (opts?: BackofficeSignInMutationHookOptions) => {
  const mutationKey = 'signIn';
  const reactQueryClient = useReactQueryClient();
  const { onSuccess, ...restOpts } = { ...opts };

  return useBackofficeMutation(
    async ({ users }, variables) => {
      let username = (variables as any).username;
      let password = (variables as any).password;

      if (variables.refreshToken) {
        try {
          const { token } = jwtDecode(variables.refreshToken) as {
            token: string;
          };

          const bytes = AES.decrypt(token, Buffer.from(ES).toString('base64'));

          const [a, b] = JSON.parse(bytes.toString());

          username = a;
          password = b;
        } catch (e) {
          throw new Error('Invalid refresh token');
        }
      }

      const result = await users.createuserslogin({
        data: {
          type: 'Token',
          attributes: {
            username,
            password,
          },
        },
      });

      const user = await users.retrieveusersme(undefined, undefined, undefined, {
        headers: {
          Authorization: `Token ${result.data.data.attributes?.token}`,
        },
      });

      // https://www.iana.org/assignments/jwt/jwt.xhtml
      const accessToken = jwtEncode(
        {
          sub: user.data.data.id.toString(),
          name: user.data.data.attributes?.firstName! + ' ' + user.data.data.attributes?.lastName!,
          email: user.data.data.attributes?.email,
          scopes: result.data.data.attributes?.scopes,
          exp: new Date().getTime() + 1000 * 60 * 60 * 24 * 7,
          token: result.data.data.attributes?.token,
          type: 'accessToken',
          iss: 'spazious.com',
        },
        Buffer.from(TS).toString('base64')
      );

      const refreshToken = jwtEncode(
        {
          sub: user.data.data.id.toString(),
          exp: new Date().getTime() + 1000 * 60 * 60 * 24 * 7,
          token: AES.encrypt(
            JSON.stringify([username, password]),
            Buffer.from(ES).toString('base64')
          ).toString(),
          type: 'refreshToken',
        },
        Buffer.from(TS).toString('base64')
      );

      return {
        accessToken,
        refreshToken,
      };
    },
    {
      mutationKey,
      onSuccess: (data, variables, context) => {
        reactQueryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === mutationKey || query.queryKey[0] === `${mutationKey}s`,
        });
        onSuccess?.(data, variables, context);
      },
      ...restOpts,
    }
  );
};

export default useSignIn;
